import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { get, getPlans } from "../../api/api"
import { useAuth } from "../../auth/AuthProvider"
import { compactNumber } from "../../utils/seo"

function Readiness({ label, ready, helper }) {
  return (
    <div className="control-item">
      <div>
        <strong>{label}</strong>
        <p className="muted">{helper}</p>
      </div>
      <span className={`status ${ready ? "active" : "warning"}`}>{ready ? "Ready" : "Action needed"}</span>
    </div>
  )
}

export default function AdminDashboard() {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === "superadmin"
  const [stats, setStats] = useState(null)
  const [billing, setBilling] = useState(null)
  const [system, setSystem] = useState(null)
  const [plans, setPlans] = useState([])
  const [error, setError] = useState("")

  useEffect(() => {
    async function load() {
      const requests = [
        get("/admin/stats"),
        get("/admin/billing"),
        getPlans(),
        isSuperAdmin ? get("/superadmin/system") : Promise.resolve({ data: null })
      ]

      const [statsRes, billingRes, plansRes, systemRes] = await Promise.allSettled(requests)

      if (statsRes.status === "fulfilled") setStats(statsRes.value.data)
      if (billingRes.status === "fulfilled") setBilling(billingRes.value.data)
      if (plansRes.status === "fulfilled") setPlans(plansRes.value.data || [])
      if (systemRes.status === "fulfilled") setSystem(systemRes.value.data)

      const failed = [statsRes, billingRes, plansRes, systemRes].find((item) => item.status === "rejected")
      if (failed) setError(failed.reason?.response?.data?.error || "Some platform data could not be loaded")
    }

    load()
  }, [isSuperAdmin])

  const provider = billing?.provider || "manual"
  const stripe = billing?.providers?.stripe || {}
  const razorpay = billing?.providers?.razorpay || {}

  return (
    <div className="page">
      <section className="hero-panel control-hero">
        <div>
          <p className="eyebrow">{isSuperAdmin ? "Super Admin HQ" : "Admin Workspace"}</p>
          <h1>Platform Control Center</h1>
          <p className="muted">
            Control users, subscriptions, plan limits, app settings, API keys, billing providers and the SEO engine.
          </p>
          <div className="actions">
            {isSuperAdmin && <Link className="button" to="/admin/settings">Open app settings</Link>}
            <Link className="button secondary" to="/admin/plans">Manage plans</Link>
            <Link className="button secondary" to="/admin/users">Manage users</Link>
          </div>
        </div>
        <div className="control-badge">
          <strong>{isSuperAdmin ? "Owner" : "Admin"}</strong>
          <span>{provider} billing</span>
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      <div className="grid">
        <div className="metric-card">
          <p className="metric-label">Users</p>
          <div className="metric">{compactNumber(stats?.users || 0)}</div>
          <p className="muted">Team and client accounts</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Projects</p>
          <div className="metric">{compactNumber(stats?.projects || 0)}</div>
          <p className="muted">Domains in the SEO engine</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Reports</p>
          <div className="metric">{compactNumber(stats?.reports || 0)}</div>
          <p className="muted">Saved audit reports</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Plans</p>
          <div className="metric">{compactNumber(plans.length)}</div>
          <p className="muted">Billing packages and limits</p>
        </div>
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Platform Readiness</h2>
          <div className="control-list">
            <Readiness label="MongoDB" ready={stats?.mongo === "connected" || system?.mongo === "connected"} helper="Database connection for users, projects and reports" />
            <Readiness label="Redis / worker queue" ready={Boolean(system?.redisConfigured)} helper="Required for background jobs and scalable crawls" />
            <Readiness label="JWT secret" ready={Boolean(system?.secrets?.jwtSecret)} helper="Required for secure authentication" />
            <Readiness label="OpenAI" ready={Boolean(system?.secrets?.openAiEnvKey)} helper="Can also be configured in encrypted app settings" />
          </div>
        </div>

        <div className="card">
          <h2>Billing Readiness</h2>
          <div className="control-list">
            <Readiness label="Plans created" ready={plans.length > 0} helper={`${plans.length} plans available`} />
            <Readiness label="Stripe" ready={stripe.publicKey && stripe.secretKey} helper="Stripe public and secret keys" />
            <Readiness label="Razorpay" ready={razorpay.keyId && razorpay.keySecret} helper="Razorpay key ID and secret" />
            <Readiness label="Payment policy" ready={Boolean(billing?.requirePayment)} helper={`${billing?.trialDays || 0} trial days, ${billing?.graceDays || 0} grace days`} />
          </div>
        </div>
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Plan Limits</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Projects</th>
                  <th>Pages</th>
                  <th>Keywords</th>
                  <th>API</th>
                </tr>
              </thead>
              <tbody>
                {plans.slice(0, 6).map((plan) => (
                  <tr key={plan._id}>
                    <td><strong>{plan.name}</strong></td>
                    <td>{billing?.currency || "USD"} {plan.price || 0}</td>
                    <td>{plan.limits?.projects || 0}</td>
                    <td>{plan.limits?.pagesCrawl || 0}</td>
                    <td>{plan.limits?.keywordsTracked || 0}</td>
                    <td>{plan.limits?.apiRequests || 0}</td>
                  </tr>
                ))}
                {plans.length === 0 && (
                  <tr>
                    <td colSpan="6" className="muted">No plans yet. Create your first plan in Plans & Limits.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h2>Recommended Setup</h2>
          <div className="insight-list">
            <div className="insight-item">
              <span className="pill high">1</span>
              <strong>Set app URL, brand and support email</strong>
              <p className="muted">This makes the product feel finished for clients.</p>
            </div>
            <div className="insight-item">
              <span className="pill high">2</span>
              <strong>Add OpenAI and SERP provider keys</strong>
              <p className="muted">Unlock AI suggestions, rank data and stronger competitor research.</p>
            </div>
            <div className="insight-item">
              <span className="pill medium">3</span>
              <strong>Create real plans with limits</strong>
              <p className="muted">Control projects, crawl pages, tracked keywords, backlinks and API usage.</p>
            </div>
            <div className="insight-item">
              <span className="pill medium">4</span>
              <strong>Run a fresh domain audit</strong>
              <p className="muted">New reports generate the full SEO Intelligence Suite.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
